import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import React, { ChangeEventHandler, MouseEvent, RefObject } from "react";

export interface AiToolInfo {
	categories: string[];
	description: string;
	icon: string;
	isPaid: boolean;
	isExtensionOnly: boolean;
	redirectId?: string;
	title: string;
	url?: string;
}

export interface BingWallpaperResponse {
	info: {
		images: {
			bot: number;
			copyright: string;
			copyrightlink: string;
			drk: number;
			enddate: string;
			fullstartdate: string;
			hs: unknown[];
			hsh: string;
			quiz: string;
			startdate: string;
			title: string;
			top: number;
			url: string;
			urlbase: string;
			wp: boolean;
		}[];
		tooltips: {
			loading: string;
			next: string;
			previous: string;
			walle: string;
			walls: string;
		};
	} | null;
	likeCount: number;
}

export interface BookmarkInfo {
	color?: string;
	hasPopup?: boolean;
	icon: string;
	id?: string;
	itemType: "action" | "bookmark" | "folder" | "preset";
	onClick?: (
		event?: React.MouseEvent<HTMLDivElement>,
	) => void | Promise<void>;
	title: string;
	url: string | null;
}

export interface BookmarkResponse {
	children: (BookmarkInfo | FolderInfo)[];
	dockItems: BookmarkInfo[];
}

export interface CornerMessageInfo extends NotificationInfo {
	buttonText?: string;
	isShown?: boolean;
}

export interface CustomInputInfo<T> {
	onChange: ChangeEventHandler<T>;
	onContextMenu: (event: MouseEvent<T>) => void;
	ref: RefObject<T | null>;
}

export const enum DialogIcon {
	ERROR,
	INFO,
	WARNING,
}

export interface DialogInfo {
	html?: string;
	icon?: DialogIcon;
	showCancel?: boolean;
	text?: string;
	title?: string;
}

export interface Divider {
	divider: boolean;
}

export interface DynamicInfo {
	alert?: string;
	blockReason?: string;
	isBadDns?: boolean;
	isTwinkstar?: boolean;
	latestExtensionVersion?: string;
	login?: string;
	redirectTo?: string;
	region?: string;
	sw?: boolean;
	tn?: TnInfo;
}

export interface EditShortcutPopupInfo {
	isShown: boolean;
	position: PositionInfo;
}

export const enum EditShortcutPopupPage {
	MAIN,
	ICON,
	FOLDER,
}

export interface ExtensionInfo extends DOMStringMap {
	login?: string;
	url: string;
	ver: string;
}

export interface FaqInfo {
	answer: string;
	question: string;
}

export interface FloatNotificationInfo extends NotificationInfo {
	toastMessage?: string;
}

export interface FolderInfo {
	children: BookmarkInfo[];
	folderName: string;
	id: string;
	itemType: "folder";
}

export const enum IconOption {
	UNKNOWN,
	AUTO,
	WEBSITE_DEFAULT,
	LIME_GENERATED,
	URL,
}

export interface GeneralSettingsInfo {
	bingWallpaperResolution?: "fhd" | "uhd";
	currentWallpaper?: string; // can be ID or URL
	is24HourTime?: boolean;
	isAirportalCoordinationEnabled?: boolean;
	isBackdropBlurEnabled?: boolean;
	isBackdropBlurEnabledForShortcuts?: boolean;
	isDockEnabled?: boolean;
	isFooterEnabled?: boolean;
	isLiteModeEnabled?: boolean;
	isQuoteEnabled?: boolean;
	isSearchHistoryEnabled?: boolean;
	language?: string;
	searchBarTabKeyAction?:
		| "lastUsedSearchEngine"
		| "nextControlInUi"
		| "nextSearchEngineInList";
	shouldAddVignettingToWallpaper?: boolean;
	shouldAutoClearSearchBar?: boolean;
	shouldFocusOnSearchBarOnLoad?: boolean;
	shouldOpenInNewPage?: boolean;
	shouldReduceMotion?: boolean;
	shouldShowGreeting?: boolean;
	shouldShowMoreShortcutsPerRow?: boolean;
	shouldShowPinnedNoteCompleteContent?: boolean;
	shouldShowSecondaryScreenOnLoad?: boolean;
	shouldShowSeconds?: boolean;
	shouldShrinkSearchBar?: boolean;
	shouldTimeTwinkle?: boolean;
	theme?: "dark" | "light" | "system";
	themeColor?: "blue" | "indigo" | "lime" | "rose" | "violet";
	timeFontWeight?: string;
}

export interface MenuInfo {
	isModal: boolean;
	items: (MenuItemInfo | Divider)[];
	shouldBeLighterInDarkTheme: boolean;
	x: number;
	y: number;
}

export interface MenuItemInfo {
	hasPopup?: boolean;
	html?: string;
	icon?: string | IconDefinition;
	id?: string;
	isDangerous?: boolean;
	isDisabled?: boolean;
	shortcut?: string;
	text: string;
}

export interface MenuItemParamInfo {
	content: MenuItemInfo | Divider;
	onClick?: (event?: React.MouseEvent<Element>) => void | Promise<void>;
	when: boolean;
}

export const enum MigrationStatus {
	NOT_NEEDED = "NOT_NEEDED",
	NOT_STARTED = "NOT_STARTED",
	QUEUED = "QUEUED",
	IN_PROGRESS = "IN_PROGRESS",
	DONE = "DONE",
	ERROR = "ERROR",
}

export interface NoteInfo {
	content: string;
	createdAt: string;
	id: string;
	isPinned?: boolean;
	itemType: "md" | "txt";
	modifiedAt: string;
	pinnedAt?: string;
	title?: string;
	username: string;
}

export interface NoteResponse {
	results: NoteInfo[];
}

export const enum NoteSavingStatus {
	UNSAVED,
	SAVING,
	SAVED,
	FAILED,
}

export interface LoginInfo {
	birthday?: string;
	email?: string;
	name?: string;
	phone?: string;
	token?: string;
	username?: string;
}

interface NotificationInfo {
	text?: string;
	title?: string;
}

export interface OptionInfo {
	text: string;
	value: string;
}

export interface PositionInfo {
	left: number;
	top: number;
}

export interface PromptInfo {
	defaultValue?: string;
	label?: string;
	type?: string;
}

export interface SearchEngineInfo {
	icon?: string;
	id: string;
	isBlockedInChina?: boolean;
	name: string;
	url: string;
	when?: boolean;
}

export interface SettingsItemInfo {
	buttonText?: string;
	colors?: {
		color: string;
		label: string;
		value: string;
	}[];
	description?: string;
	isChecked?: boolean;
	isMasterReset?: boolean;
	key?: string;
	labels?: Record<string, string>;
	onChange?: (newValue: unknown) => void | Promise<void>;
	onClick?: (event: MouseEvent<HTMLButtonElement>) => void | Promise<void>;
	options?: string[];
	selectedValue?: string;
	shouldWrapOnMobile?: boolean;
	title: string;
	type: SettingsItemType;
	warning?: SettingsItemWarning;
}

export const enum SettingsItemType {
	BUTTON,
	COLOR,
	GROUP_TITLE,
	SELECT,
	SWITCH,
}

export const enum SettingsItemWarning {
	BROWSER_INCOMPATIBLE,
	CONTROLLED_BY_EXTENSION,
	INVALID,
}

export interface SiteInfoResponse {
	icon: string;
	title: string;
}

export interface TnInfo {
	ch?: string;
	tn: string;
}
